import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, Component, Input, EventEmitter, Output, ViewChildren, ViewChild, HostListener, Directive } from '@angular/core';
import { BehaviorSubject, Subject, map, catchError, Subscription, of, timer, takeWhile, Observable, throwError, switchMap as switchMap$1 } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import * as i1 from 'angular-auth-oidc-client';
import * as i1$1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import * as i4 from 'primeng/api';
import { PrimeIcons } from 'primeng/api';
import * as i1$2 from 'primeng/overlaypanel';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import * as i1$3 from '@angular/common';
import { CommonModule, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ButtonComponent, TabMenuComponent, ProgressBarComponent } from '@accesso/ng-uds';
import * as i3 from 'primeng/menubar';
import { MenubarModule } from 'primeng/menubar';
import * as i3$1 from 'primeng/dialog';
import { DialogModule } from 'primeng/dialog';
import { MarkdownComponent } from 'ngx-markdown';
import * as i1$4 from '@angular/common/http';
import * as i2 from 'primeng/messages';
import { MessagesModule } from 'primeng/messages';
import SwaggerUI from 'swagger-ui';
import * as i1$5 from 'primeng/dropdown';
import { DropdownModule } from 'primeng/dropdown';
import * as i2$1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { map as map$1, tap, takeUntil, catchError as catchError$1, switchMap } from 'rxjs/operators';
import * as i2$2 from 'primeng/dynamicdialog';

var IDP;
(function (IDP) {
    IDP["OIDC"] = "oidc";
})(IDP || (IDP = {}));

class AbstractUserService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AbstractUserService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AbstractUserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AbstractUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

var UserGroups;
(function (UserGroups) {
    UserGroups["DEVELOPER"] = "developer";
    UserGroups["CLIENT_ADMIN"] = "client-admin";
    UserGroups["SUPER_ADMIN"] = "super-admin";
    UserGroups["OWNER"] = "owner";
    UserGroups["PRODUCT_ADMIN"] = "product-admin";
})(UserGroups || (UserGroups = {}));
const defaultUser = {
    id: '',
    name: '',
    email: '',
    avatar: '',
    clientId: '',
    isAuthenticated: false,
    cognitoGroups: [],
};

const APP_ENVIRONMENT = new InjectionToken('environment');
class EnvironmentService {
    constructor(environmentProperties) {
        this.environmentProperties = environmentProperties;
    }
    getEnvironmentProperties() {
        return this.environmentProperties;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: EnvironmentService, deps: [{ token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: EnvironmentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: EnvironmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

/**
 * Validates if a string is a valid URL
 * @param url - The string to validate
 * @returns boolean indicating if the string is a valid URL
 */
function isValidUrl(url) {
    const urlPattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i');
    return urlPattern.test(url);
}

const LogLevel = {
    DEBUG: { id: 'DEBUG', name: 'debug', precedence: 0 },
    INFO: { id: 'INFO', name: 'info', precedence: 10 },
    WARN: { id: 'WARN', name: 'warn', precedence: 20 },
    ERROR: { id: 'ERROR', name: 'error', precedence: 30 },
};

/* eslint-disable @typescript-eslint/no-explicit-any */
class Logger {
    constructor(environmnet) {
        this.environmnet = environmnet;
    }
    log(level, message, ...messageParams) {
        message = '[' + level.id + ']: ' + message;
        switch (level) {
            case LogLevel.DEBUG:
                this.debug(message, messageParams);
                return;
            case LogLevel.INFO:
                this.info(message, messageParams);
                return;
            case LogLevel.WARN:
                this.warn(message, messageParams);
                return;
            case LogLevel.ERROR:
                this.error(message, messageParams);
                return;
            default:
                this.debug(message, messageParams);
        }
    }
    debug(message, ...messageParams) {
        if (this.shouldLog('DEBUG')) {
            console.debug(message, messageParams);
        }
    }
    info(message, ...messageParams) {
        if (this.shouldLog('INFO')) {
            console.info(message, messageParams);
        }
    }
    warn(message, ...messageParams) {
        if (this.shouldLog('WARN')) {
            console.warn(message, messageParams);
        }
    }
    error(message, ...messageParams) {
        console.error(message, messageParams);
    }
    shouldLog(level) {
        const id = this.environmnet.logLevel.id;
        return LogLevel[level].precedence >= LogLevel[id].precedence;
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class LoggingService {
    constructor(environment) {
        this.environment = environment;
        this.logger = new Logger(environment.getEnvironmentProperties());
    }
    debug(message, ...messageParams) {
        this.logger.log(LogLevel.DEBUG, message, messageParams);
    }
    info(message, ...messageParams) {
        this.logger.log(LogLevel.INFO, message, messageParams);
    }
    warn(message, ...messageParams) {
        this.logger.log(LogLevel.WARN, message, messageParams);
    }
    error(message, ...messageParams) {
        this.logger.log(LogLevel.ERROR, message, messageParams);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggingService, deps: [{ token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

class AuthService {
    constructor(oidcSecurityService) {
        this.oidcSecurityService = oidcSecurityService;
        this._isInternalLogin = new BehaviorSubject(false);
        this._isLoggedIn = new BehaviorSubject(false);
        this._user = new BehaviorSubject(defaultUser);
    }
    get user() {
        return this._user.getValue();
    }
    set user(value) {
        this._user.next(value);
    }
    getAuthProvider() {
        return IDP.OIDC;
    }
    get isInternalLogin() {
        return this._isInternalLogin.getValue();
    }
    set isInternalLogin(value) {
        this._isInternalLogin.next(value);
    }
    set isLoggedIn(value) {
        this._isLoggedIn.next(value);
    }
    get isLoggedIn() {
        return this._isLoggedIn.getValue();
    }
    logout() {
        this._isLoggedIn.next(false);
    }
    getLoggedInEvent() {
        return this._isLoggedIn.asObservable();
    }
    getIDToken() {
        return this.oidcSecurityService.getIdToken();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthService, deps: [{ token: i1.OidcSecurityService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.OidcSecurityService }] });

const STANDARD_MESSAGE_LIFE = 8000;
class NotificationsService {
    constructor() {
        this.messageNotificationChange = new Subject();
        this.message = [];
    }
    messageNotifyNow() {
        this.messageNotificationChange.next(this.message);
        this.message = [];
    }
    notificationClear() {
        this.message = [];
        this.messageNotifyNow();
    }
    messageNotify(severity, detail, sticky = false) {
        if (sticky) {
            this.messageNotificationChange.next({ severity, detail, icon: this.getIcon(severity) });
        }
        else {
            this.messageNotificationChange.next({ severity, detail, icon: this.getIcon(severity), life: STANDARD_MESSAGE_LIFE, sticky: sticky });
        }
    }
    addNotification(severity, detail, life = STANDARD_MESSAGE_LIFE, sticky = false) {
        this.message.push({ severity, detail, icon: this.getIcon(severity), life: life, sticky: sticky });
    }
    getIcon(severity) {
        switch (severity) {
            case 'success':
                return PrimeIcons.CHECK;
            case 'info':
                return PrimeIcons.INFO;
            case 'warn':
                return PrimeIcons.EXCLAMATION_TRIANGLE;
            case 'error':
                return PrimeIcons.EXCLAMATION_TRIANGLE;
            default:
                return PrimeIcons.INFO;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class OidcUserService extends AbstractUserService {
    constructor(oidcSecurityService, logger, authService, router, environment, notificationsService) {
        super();
        this.oidcSecurityService = oidcSecurityService;
        this.logger = logger;
        this.authService = authService;
        this.router = router;
        this.environment = environment;
        this.notificationsService = notificationsService;
    }
    getUser() {
        try {
            return this.oidcSecurityService.checkAuth().pipe(map((userData) => {
                this.logger.debug('oidcSecurityService.checkAuth() => User:', userData);
                let userGet = defaultUser;
                if (userData.isAuthenticated) {
                    userGet = this.mapAuthUserToUser(userData);
                }
                this.authService.user = userGet;
                this.authService.isLoggedIn = userData.isAuthenticated;
                return userGet;
            }));
        }
        catch (e) {
            this.logger.error('Failed to fetch OIDC user:', e);
            throw e;
        }
    }
    mapAuthUserToUser(authUser) {
        this.logger.debug('OidcUserService.mapAuthUserToUser.authUser:', authUser);
        const user = defaultUser;
        user.id = authUser.userData.sub;
        user.name = authUser.userData.name || 'Unknown User';
        user.email = authUser.userData.email || 'No email';
        user.idToken = authUser.idToken;
        user.avatar = isValidUrl(authUser.userData.picture) ? authUser.userData.picture : '';
        user.clientId = authUser.userData['custom:companyName'] || '';
        user.userType = authUser.userData.userType || '';
        user.isAuthenticated = authUser.isAuthenticated;
        const decodedUser = jwtDecode(authUser.idToken);
        user.cognitoGroups = decodedUser['cognito:groups'] || [];
        this.logger.debug('OidcUserService.mapAuthUserToUser.user:', user);
        return user;
    }
    logIn() {
        this.oidcSecurityService.authorize();
    }
    logOut(logOffLocal, logOffMessage) {
        this.authService.logout();
        if (logOffLocal && logOffMessage) {
            this.oidcSecurityService.logoffLocal();
            this.router.navigate(['/']).then(() => {
                this.notificationsService.messageNotify('warn', logOffMessage, true);
            });
        }
        else {
            const options = {
                logoffMethod: 'GET',
                customParams: {
                    client_id: this.environment.getEnvironmentProperties().oidcConfig.config.clientId,
                    logout_uri: this.environment.getEnvironmentProperties().oidcConfig.config.postLogoutRedirectUri,
                    response_type: 'code',
                },
            };
            this.oidcSecurityService.logoff(undefined, options).subscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OidcUserService, deps: [{ token: i1.OidcSecurityService }, { token: LoggingService }, { token: AuthService }, { token: i1$1.Router }, { token: APP_ENVIRONMENT }, { token: NotificationsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OidcUserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OidcUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.OidcSecurityService }, { type: LoggingService }, { type: AuthService }, { type: i1$1.Router }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }, { type: NotificationsService }] });

class UserServiceFactory {
    constructor(oidcUserFactory) {
        this.oidcUserFactory = oidcUserFactory;
    }
    getUserService(serviceType) {
        switch (serviceType) {
            case IDP.OIDC:
                return this.oidcUserFactory;
            default:
                throw new Error(`Service Type Unknown: ${serviceType}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: UserServiceFactory, deps: [{ token: OidcUserService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: UserServiceFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: UserServiceFactory, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: OidcUserService }] });

class LoadingService {
    constructor() {
        this.loadingSubject = new BehaviorSubject(false);
        this.loading$ = this.loadingSubject.asObservable();
    }
    loadingOn() {
        this.loadingSubject.next(true);
    }
    loadingOff() {
        this.loadingSubject.next(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoadingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoadingService, providedIn: "root" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoadingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }] });

class OidcCallbackComponent {
    constructor(userServiceFactory, router, loadingService) {
        this.userServiceFactory = userServiceFactory;
        this.router = router;
        this.loadingService = loadingService;
        this.error = '';
    }
    ngOnInit() {
        this.loadingService.loadingOn();
        this.userServiceFactory
            .getUserService(IDP.OIDC)
            .getUser()
            .subscribe({
            next: () => {
                this.loadingService.loadingOff();
            },
            error: () => {
                this.loadingService.loadingOff();
                this.router.navigate(['/error'], {
                    queryParams: {
                        type: '500',
                        title: 'Login Error',
                        desc: 'An error occurred while logging in. Please try logging in again later.',
                    },
                });
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OidcCallbackComponent, deps: [{ token: UserServiceFactory }, { token: i1$1.Router }, { token: LoadingService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: OidcCallbackComponent, isStandalone: true, selector: "app-oidc-callback", ngImport: i0, template: `<div>{{ error }}</div>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OidcCallbackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'app-oidc-callback',
                    standalone: true,
                    imports: [],
                    template: `<div>{{ error }}</div>`,
                }]
        }], ctorParameters: () => [{ type: UserServiceFactory }, { type: i1$1.Router }, { type: LoadingService }] });

class HomeComponent {
    constructor(environment) {
        this.environment = environment;
        this.headerMain = '';
        this.headerSub = '';
        this.bodyHeader = '';
        this.bodyContent = '';
        this.baseS3Url = '';
    }
    ngOnInit() {
        this.baseS3Url = `${this.environment.getEnvironmentProperties().s3BucketBaseUrl}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: HomeComponent, deps: [{ token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: HomeComponent, isStandalone: true, selector: "app-home", inputs: { headerMain: "headerMain", headerSub: "headerSub", bodyHeader: "bodyHeader", bodyContent: "bodyContent" }, ngImport: i0, template: "<div class=\"header\" [style.background-image]=\"'url(' + baseS3Url + '/img/other/home-page-bg-black.png)'\">\n  <div class=\"header-logo-box\">\n    <img\n      [src]=\"baseS3Url + '/img/logo/accesso-logo-white.png'\"\n      alt=\"Logo\"\n      class=\"header-logo\"\n    />\n  </div>\n  <div class=\"header-text-box\">\n    <h2 class=\"heading-primary\">\n      <span class=\"heading-primary-main\">{{headerMain}}</span>\n      <h3 class=\"heading-primary-sub\">\n        {{headerSub}}\n      </h3>\n    </h2>\n  </div>\n</div>\n<div class=\"body\">\n  <div class=\"body-header\">\n    <h3>{{bodyHeader}}</h3>\n    <div class=\"body-1 body-content\">\n      {{bodyContent}}\n    </div>\n  </div>\n</div>\n", styles: [".header{display:flex;align-items:center;justify-content:center;flex-direction:column;height:50%;background-size:cover}.header .logo-box{width:208px}.header .heading-primary{display:flex;flex-direction:column;align-items:center;justify-content:center;color:#fff;text-align:center}.header .heading-primary-main{margin-bottom:50px;font-weight:200;letter-spacing:4px}.header .heading-primary-sub{font-size:16px;font-weight:300;letter-spacing:1px}.body{display:flex;align-items:center;flex-direction:row;padding:60px}.body-header{width:30%;display:flex;flex-direction:column;font-weight:500}.body-content{margin-top:20px;font-weight:200;font-size:20px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: HomeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-home', standalone: true, imports: [], template: "<div class=\"header\" [style.background-image]=\"'url(' + baseS3Url + '/img/other/home-page-bg-black.png)'\">\n  <div class=\"header-logo-box\">\n    <img\n      [src]=\"baseS3Url + '/img/logo/accesso-logo-white.png'\"\n      alt=\"Logo\"\n      class=\"header-logo\"\n    />\n  </div>\n  <div class=\"header-text-box\">\n    <h2 class=\"heading-primary\">\n      <span class=\"heading-primary-main\">{{headerMain}}</span>\n      <h3 class=\"heading-primary-sub\">\n        {{headerSub}}\n      </h3>\n    </h2>\n  </div>\n</div>\n<div class=\"body\">\n  <div class=\"body-header\">\n    <h3>{{bodyHeader}}</h3>\n    <div class=\"body-1 body-content\">\n      {{bodyContent}}\n    </div>\n  </div>\n</div>\n", styles: [".header{display:flex;align-items:center;justify-content:center;flex-direction:column;height:50%;background-size:cover}.header .logo-box{width:208px}.header .heading-primary{display:flex;flex-direction:column;align-items:center;justify-content:center;color:#fff;text-align:center}.header .heading-primary-main{margin-bottom:50px;font-weight:200;letter-spacing:4px}.header .heading-primary-sub{font-size:16px;font-weight:300;letter-spacing:1px}.body{display:flex;align-items:center;flex-direction:row;padding:60px}.body-header{width:30%;display:flex;flex-direction:column;font-weight:500}.body-content{margin-top:20px;font-weight:200;font-size:20px}\n"] }]
        }], ctorParameters: () => [{ type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }], propDecorators: { headerMain: [{
                type: Input
            }], headerSub: [{
                type: Input
            }], bodyHeader: [{
                type: Input
            }], bodyContent: [{
                type: Input
            }] } });

class ClipboardCopyComponent {
    constructor() {
        this.text = '';
    }
    copyToClipboard() {
        navigator.clipboard.writeText(this.text);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ClipboardCopyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: ClipboardCopyComponent, isStandalone: true, selector: "app-clipboard-copy", inputs: { text: "text" }, ngImport: i0, template: "<!-- eslint-disable @angular-eslint/template/click-events-have-key-events -->\n<!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->\n<span class=\"clipboard-copy\" (click)=\"copyToClipboard()\">\n    <span (click)=\"op.toggle($event)\"><i class=\"clipboard-copy-icon pi pi-clone\"></i></span>\n    <p-overlayPanel #op>\n        <div class=\"clipboard-overlay\">\n            <div class=\"body-1 clipboard-overlay-text\">\n              <span><i class=\"pi pi-check\"></i></span> Copied!\n            </div>\n        </div>\n    </p-overlayPanel>      \n</span>", styles: [".clipboard-copy{color:#000;cursor:pointer;margin-left:5px}.clipboard-copy-icon{font-size:16px}.clipboard-overlay{padding:10px}.clipboard-overlay-text{color:green}\n"], dependencies: [{ kind: "ngmodule", type: OverlayPanelModule }, { kind: "component", type: i1$2.OverlayPanel, selector: "p-overlayPanel", inputs: ["ariaLabel", "ariaLabelledBy", "dismissable", "showCloseIcon", "style", "styleClass", "appendTo", "autoZIndex", "ariaCloseLabel", "baseZIndex", "focusOnShow", "showTransitionOptions", "hideTransitionOptions"], outputs: ["onShow", "onHide"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ClipboardCopyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-clipboard-copy', standalone: true, imports: [OverlayPanelModule], template: "<!-- eslint-disable @angular-eslint/template/click-events-have-key-events -->\n<!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->\n<span class=\"clipboard-copy\" (click)=\"copyToClipboard()\">\n    <span (click)=\"op.toggle($event)\"><i class=\"clipboard-copy-icon pi pi-clone\"></i></span>\n    <p-overlayPanel #op>\n        <div class=\"clipboard-overlay\">\n            <div class=\"body-1 clipboard-overlay-text\">\n              <span><i class=\"pi pi-check\"></i></span> Copied!\n            </div>\n        </div>\n    </p-overlayPanel>      \n</span>", styles: [".clipboard-copy{color:#000;cursor:pointer;margin-left:5px}.clipboard-copy-icon{font-size:16px}.clipboard-overlay{padding:10px}.clipboard-overlay-text{color:green}\n"] }]
        }], propDecorators: { text: [{
                type: Input
            }] } });

var TooltipPosition;
(function (TooltipPosition) {
    TooltipPosition["ABOVE"] = "above";
    TooltipPosition["BELOW"] = "below";
    TooltipPosition["LEFT"] = "left";
    TooltipPosition["RIGHT"] = "right";
    TooltipPosition["DYNAMIC"] = "dynamic";
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    TooltipPosition["DEFAULT"] = "above";
})(TooltipPosition || (TooltipPosition = {}));

class TooltipComponent {
    constructor() {
        this.position = TooltipPosition.ABOVE;
        this.tooltip = '';
        this.left = 0;
        this.top = 0;
        this.visible = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: TooltipComponent, isStandalone: true, selector: "tooltip", ngImport: i0, template: "<div class=\"caption tooltip\" [ngClass]=\"['tooltip--' + position]\" [class.tooltip--visible]=\"visible\" [style.left]=\"left + 'px'\" [style.top]=\"top + 'px'\">\n\t{{ tooltip }}\n</div>\n", styles: [".tooltip{position:fixed;background-color:#000;border-radius:8px;color:#fff;z-index:999!important;padding:8px;opacity:0}.tooltip:before{content:\"\";width:0;height:0;position:absolute}.tooltip--visible{opacity:1;transition:opacity .3s}.tooltip--below{transform:translate(-50%);margin-top:7px}.tooltip--below:before{border-left-color:transparent;border-right-color:transparent;border-top:none;left:calc(50% - 5px);top:-5px}.tooltip--above{transform:translate(-50%,-100%);margin-bottom:7px}.tooltip--above:before{border-left-color:transparent;border-right-color:transparent;border-bottom:none;left:calc(50% - 5px);bottom:-5px}.tooltip--left{transform:translate(calc(-100% - 7px),-50%)}.tooltip--left:before{border-top-color:transparent;border-bottom-color:transparent;border-right:none;right:-5px;top:calc(50% - 5px)}.tooltip--right{transform:translateY(-50%);margin-left:7px}.tooltip--right:before{border-top-color:transparent;border-bottom-color:transparent;border-left:none;left:-5px;top:calc(50% - 5px)}.tooltip--dynamic{margin-left:20px}.tooltip--dynamic:before{display:none}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tooltip', standalone: true, imports: [CommonModule], template: "<div class=\"caption tooltip\" [ngClass]=\"['tooltip--' + position]\" [class.tooltip--visible]=\"visible\" [style.left]=\"left + 'px'\" [style.top]=\"top + 'px'\">\n\t{{ tooltip }}\n</div>\n", styles: [".tooltip{position:fixed;background-color:#000;border-radius:8px;color:#fff;z-index:999!important;padding:8px;opacity:0}.tooltip:before{content:\"\";width:0;height:0;position:absolute}.tooltip--visible{opacity:1;transition:opacity .3s}.tooltip--below{transform:translate(-50%);margin-top:7px}.tooltip--below:before{border-left-color:transparent;border-right-color:transparent;border-top:none;left:calc(50% - 5px);top:-5px}.tooltip--above{transform:translate(-50%,-100%);margin-bottom:7px}.tooltip--above:before{border-left-color:transparent;border-right-color:transparent;border-bottom:none;left:calc(50% - 5px);bottom:-5px}.tooltip--left{transform:translate(calc(-100% - 7px),-50%)}.tooltip--left:before{border-top-color:transparent;border-bottom-color:transparent;border-right:none;right:-5px;top:calc(50% - 5px)}.tooltip--right{transform:translateY(-50%);margin-left:7px}.tooltip--right:before{border-top-color:transparent;border-bottom-color:transparent;border-left:none;left:-5px;top:calc(50% - 5px)}.tooltip--dynamic{margin-left:20px}.tooltip--dynamic:before{display:none}\n"] }]
        }] });

class NavbarComponent {
    constructor(authService, userServiceFactory, environment) {
        this.authService = authService;
        this.userServiceFactory = userServiceFactory;
        this.environment = environment;
        this.isUserLoggedIn = false;
        this.baseS3Url = '';
        this.userIcon = '';
        this.items = [];
    }
    ngOnInit() {
        this.isUserLoggedIn = this.authService.isLoggedIn;
        this.user = this.authService.user;
        this.baseS3Url = this.environment.getEnvironmentProperties().s3BucketBaseUrl;
        this.userIcon = this.user.avatar !== '' ? this.user.avatar : this.baseS3Url + '/img/svg/human.svg';
        this.loggedInSubscription = this.authService.getLoggedInEvent().subscribe((loggedIn) => {
            this.isUserLoggedIn = loggedIn;
            this.user = this.authService.user;
            this.userIcon = this.user.avatar !== '' ? this.user.avatar : this.baseS3Url + '/img/svg/human.svg';
        });
    }
    navigateToLogin() {
        this.userServiceFactory
            .getUserService(this.authService.getAuthProvider())
            .logIn();
    }
    onLogout() {
        this.userServiceFactory
            .getUserService(this.authService.getAuthProvider())
            .logOut();
    }
    ngOnDestroy() {
        this.loggedInSubscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NavbarComponent, deps: [{ token: AuthService }, { token: UserServiceFactory }, { token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: NavbarComponent, isStandalone: true, selector: "app-navbar", ngImport: i0, template: "<!-- TODO fix lint-->\n<!-- eslint-disable @angular-eslint/template/click-events-have-key-events -->\n<!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->\n@if(isUserLoggedIn) {\n  <p-menubar [model]=\"items\">\n      <ng-template pTemplate=\"start\" class=\"logo\">\n        <img\n          [src]=\"baseS3Url + '/img/logo/accesso-logo.png'\"\n          [alt]=\"'accesso logo'\"\n          width=\"79\"\n          height=\"40\"\n          class=\"logo\"\n        />\n      </ng-template>\n      <ng-template pTemplate=\"end\">\n        <img\n        [src]=\"userIcon\"\n        [alt]=\"'user icon'\"\n        width=\"25\"\n        height=\"25\"\n        class=\"profile-icon\"\n        (click)=\"op.toggle($event)\"\n        (error)=\"userIcon = baseS3Url + '/img/svg/human.svg'\"\n        />\n        <div class=\"profile-overlay\">\n          <p-overlayPanel #op>\n            <div class=\"user-detail\">\n              @if (user) {\n              <img\n                [src]=\"userIcon\"\n                width=\"50\"\n                height=\"50\"\n                [alt]=\"'user icon'\"\n              />\n              <div class=\"user-info\">\n                <h5 class=\"name\">{{ user.name }}</h5>\n                <div class=\"body-2 email\">{{ user.email }}</div>\n              </div>\n              }\n            </div>\n            <div class=\"logout\">\n              <ul>\n                <li (click)=\"onLogout()\">\n                  <i class=\"pi pi-sign-out\"></i>\n                  <a>Logout</a>\n                </li>\n              </ul>\n            </div>\n          </p-overlayPanel>\n        </div>\n      </ng-template>\n  </p-menubar>\n}\n@if(!isUserLoggedIn) {\n  <p-menubar [model]=\"items\">\n      <ng-template pTemplate=\"start\" class=\"logo\">\n        <img\n          [src]=\"baseS3Url + '/img/logo/accesso-logo.png'\"\n          width=\"79\"\n          height=\"40\"\n          class=\"logo\"\n          [alt]=\"'accesso logo'\"\n        />\n      </ng-template>\n      <ng-template pTemplate=\"end\">\n        <ud-button [label]=\"'Login'\" (onClick)=\"navigateToLogin()\"></ud-button>\n      </ng-template>\n  </p-menubar>\n}\n", styles: [".logo{margin-right:16px}.user-detail{display:flex;align-items:center;justify-content:center;flex-direction:column;padding:1rem;gap:10px}.user-detail img{border-radius:100%}.user-detail .user-info{display:flex;flex-direction:column;align-items:center;justify-content:center}.profile-icon{cursor:pointer;border-radius:100%}.logout{border-top:1px solid var(--footer-border-color, var(--g4));padding-left:1rem}.logout ul{list-style-type:none;padding:0}.logout ul li{color:var(--g1);text-decoration:none}.logout ul li:hover{color:var(--theme-secondary);cursor:pointer}.logout ul a{color:var(--g1);text-decoration:none;padding-left:10px}.logout ul a:hover{color:var(--theme-secondary);cursor:pointer}::ng-deep .p-overlaypanel .p-overlaypanel-content{padding:0}\n"], dependencies: [{ kind: "ngmodule", type: MenubarModule }, { kind: "component", type: i3.Menubar, selector: "p-menubar", inputs: ["model", "style", "styleClass", "autoZIndex", "baseZIndex", "autoDisplay", "autoHide", "autoHideDelay", "id", "ariaLabel", "ariaLabelledBy"], outputs: ["onFocus", "onBlur"] }, { kind: "directive", type: i4.PrimeTemplate, selector: "[pTemplate]", inputs: ["type", "pTemplate"] }, { kind: "component", type: ButtonComponent, selector: "ud-button", inputs: ["label", "ariaLabel", "icon", "loading", "isPrimary", "noWrap", "iconPos", "palette", "disabled", "variant", "layout", "type"], outputs: ["onClick", "onFocus", "onBlur"] }, { kind: "ngmodule", type: OverlayPanelModule }, { kind: "component", type: i1$2.OverlayPanel, selector: "p-overlayPanel", inputs: ["ariaLabel", "ariaLabelledBy", "dismissable", "showCloseIcon", "style", "styleClass", "appendTo", "autoZIndex", "ariaCloseLabel", "baseZIndex", "focusOnShow", "showTransitionOptions", "hideTransitionOptions"], outputs: ["onShow", "onHide"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NavbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-navbar', standalone: true, imports: [NgIf, NgFor, MenubarModule, ButtonComponent, OverlayPanelModule], template: "<!-- TODO fix lint-->\n<!-- eslint-disable @angular-eslint/template/click-events-have-key-events -->\n<!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->\n@if(isUserLoggedIn) {\n  <p-menubar [model]=\"items\">\n      <ng-template pTemplate=\"start\" class=\"logo\">\n        <img\n          [src]=\"baseS3Url + '/img/logo/accesso-logo.png'\"\n          [alt]=\"'accesso logo'\"\n          width=\"79\"\n          height=\"40\"\n          class=\"logo\"\n        />\n      </ng-template>\n      <ng-template pTemplate=\"end\">\n        <img\n        [src]=\"userIcon\"\n        [alt]=\"'user icon'\"\n        width=\"25\"\n        height=\"25\"\n        class=\"profile-icon\"\n        (click)=\"op.toggle($event)\"\n        (error)=\"userIcon = baseS3Url + '/img/svg/human.svg'\"\n        />\n        <div class=\"profile-overlay\">\n          <p-overlayPanel #op>\n            <div class=\"user-detail\">\n              @if (user) {\n              <img\n                [src]=\"userIcon\"\n                width=\"50\"\n                height=\"50\"\n                [alt]=\"'user icon'\"\n              />\n              <div class=\"user-info\">\n                <h5 class=\"name\">{{ user.name }}</h5>\n                <div class=\"body-2 email\">{{ user.email }}</div>\n              </div>\n              }\n            </div>\n            <div class=\"logout\">\n              <ul>\n                <li (click)=\"onLogout()\">\n                  <i class=\"pi pi-sign-out\"></i>\n                  <a>Logout</a>\n                </li>\n              </ul>\n            </div>\n          </p-overlayPanel>\n        </div>\n      </ng-template>\n  </p-menubar>\n}\n@if(!isUserLoggedIn) {\n  <p-menubar [model]=\"items\">\n      <ng-template pTemplate=\"start\" class=\"logo\">\n        <img\n          [src]=\"baseS3Url + '/img/logo/accesso-logo.png'\"\n          width=\"79\"\n          height=\"40\"\n          class=\"logo\"\n          [alt]=\"'accesso logo'\"\n        />\n      </ng-template>\n      <ng-template pTemplate=\"end\">\n        <ud-button [label]=\"'Login'\" (onClick)=\"navigateToLogin()\"></ud-button>\n      </ng-template>\n  </p-menubar>\n}\n", styles: [".logo{margin-right:16px}.user-detail{display:flex;align-items:center;justify-content:center;flex-direction:column;padding:1rem;gap:10px}.user-detail img{border-radius:100%}.user-detail .user-info{display:flex;flex-direction:column;align-items:center;justify-content:center}.profile-icon{cursor:pointer;border-radius:100%}.logout{border-top:1px solid var(--footer-border-color, var(--g4));padding-left:1rem}.logout ul{list-style-type:none;padding:0}.logout ul li{color:var(--g1);text-decoration:none}.logout ul li:hover{color:var(--theme-secondary);cursor:pointer}.logout ul a{color:var(--g1);text-decoration:none;padding-left:10px}.logout ul a:hover{color:var(--theme-secondary);cursor:pointer}::ng-deep .p-overlaypanel .p-overlaypanel-content{padding:0}\n"] }]
        }], ctorParameters: () => [{ type: AuthService }, { type: UserServiceFactory }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

class MarkdownEventService {
    constructor() {
        this.markDownLoadEvent = new BehaviorSubject(false);
    }
    emitMarkDownLoadEvent(value) {
        this.markDownLoadEvent.next(value);
    }
    getMarkDownLoadEvent() {
        return this.markDownLoadEvent.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: MarkdownEventService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: MarkdownEventService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: MarkdownEventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AppMarkdownComponent {
    constructor(markdownEventService) {
        this.markdownEventService = markdownEventService;
    }
    onload() {
        this.markdownEventService.emitMarkDownLoadEvent(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AppMarkdownComponent, deps: [{ token: MarkdownEventService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: AppMarkdownComponent, isStandalone: true, selector: "app-markdown", inputs: { markdownUrl: "markdownUrl" }, ngImport: i0, template: "<div class=\"markdown-wrapper markdown-body\">\n  <markdown [src]=\"markdownUrl\" (load)=\"onload()\"> </markdown>\n</div>\n", styles: [":host ::ng-deep .markdown-body blockquote{padding:1rem}\n"], dependencies: [{ kind: "component", type: MarkdownComponent, selector: "markdown, [markdown]", inputs: ["data", "src", "disableSanitizer", "inline", "clipboard", "clipboardButtonComponent", "clipboardButtonTemplate", "emoji", "katex", "katexOptions", "mermaid", "mermaidOptions", "lineHighlight", "line", "lineOffset", "lineNumbers", "start", "commandLine", "filterOutput", "host", "prompt", "output", "user"], outputs: ["error", "load", "ready"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AppMarkdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-markdown', standalone: true, imports: [MarkdownComponent], template: "<div class=\"markdown-wrapper markdown-body\">\n  <markdown [src]=\"markdownUrl\" (load)=\"onload()\"> </markdown>\n</div>\n", styles: [":host ::ng-deep .markdown-body blockquote{padding:1rem}\n"] }]
        }], ctorParameters: () => [{ type: MarkdownEventService }], propDecorators: { markdownUrl: [{
                type: Input
            }] } });

class TermsAndConditionService {
    constructor(http, logger, environment) {
        this.http = http;
        this.logger = logger;
        this.environment = environment;
        this._acceptedLatestTerms = new BehaviorSubject(false);
        this.baseUrl = `${environment.getEnvironmentProperties().adkPortalApiBaseUrl}/v1`;
    }
    getAcceptedLatest() {
        return this.http
            .get(`${this.baseUrl}/user/terms/accepted/latest`)
            .pipe(catchError((error) => {
            this.logger.error('Failed to fetch latest terms accepted:', error);
            throw error;
        }));
    }
    postAcceptedLatest() {
        const data = {};
        return this.http
            .post(`${this.baseUrl}/user/terms/accepted/latest`, data)
            .pipe(catchError((error) => {
            this.logger.error('Failed to accept terms:', error);
            throw error;
        }));
    }
    getlatestTermsAndConditionData() {
        return this.http.get(`${this.baseUrl}/terms/latest`).pipe(catchError((error) => {
            this.logger.error('Failed to fetch terms and condition data:', error);
            throw error;
        }));
    }
    get acceptedLatestTerms() {
        return this._acceptedLatestTerms.getValue();
    }
    set acceptedLatestTerms(value) {
        this._acceptedLatestTerms.next(value);
    }
    getTermsAndConditionAcceptanceEvent() {
        return this._acceptedLatestTerms.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionService, deps: [{ token: i1$4.HttpClient }, { token: LoggingService }, { token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$4.HttpClient }, { type: LoggingService }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

class TermsAndConditionComponent {
    constructor(router, elementRef, termsAndConditionService, environment) {
        this.router = router;
        this.elementRef = elementRef;
        this.termsAndConditionService = termsAndConditionService;
        this.environment = environment;
        this.closeButtonEnabled = false;
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onClose = new EventEmitter();
        this.modalVisible = true;
        this.disableBtn = true;
        this.acceptedTerms = false;
        this.url = '';
    }
    ngOnInit() {
        this.termsAndConditionService
            .getlatestTermsAndConditionData()
            .subscribe((terms) => {
            if (terms && terms.url) {
                this.url = terms.url;
            }
        });
    }
    ngAfterViewInit() {
        this.setupForceScrollListener();
    }
    navigateToLanding() {
        this.modalVisible = false;
        this.termsAndConditionService.acceptedLatestTerms = true;
        this.termsAndConditionService.postAcceptedLatest().subscribe();
        this.router.navigate([this.environment.getEnvironmentProperties().defaultLandingRoute]);
    }
    closeModal() {
        this.modalVisible = false;
        this.onClose.emit();
    }
    setupForceScrollListener() {
        const pDialogContent = this.elementRef.nativeElement.querySelector('.p-dialog-content');
        if (pDialogContent) {
            pDialogContent.addEventListener('scroll', () => {
                // Check if the user scrolled position is at the bottom of the content
                const isAtBottom = pDialogContent.scrollHeight - pDialogContent.clientHeight <=
                    pDialogContent.scrollTop + 1;
                if (this.disableBtn && isAtBottom) {
                    this.disableBtn = false;
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionComponent, deps: [{ token: i1$1.Router }, { token: i0.ElementRef }, { token: TermsAndConditionService }, { token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: TermsAndConditionComponent, isStandalone: true, selector: "app-terms-and-condition", inputs: { closeButtonEnabled: "closeButtonEnabled" }, outputs: { onClose: "onClose" }, ngImport: i0, template: "<p-dialog\n  [showHeader]=\"true\"\n  header=\"Terms and Conditions\"\n  [modal]=\"true\"\n  [draggable]=\"false\"\n  [closeOnEscape]=\"false\"\n  [(visible)]=\"modalVisible\"\n  [closable]=\"closeButtonEnabled\"\n  (onHide) = \"closeModal()\"\n  [style]=\"{ width: '50rem' }\"\n  [breakpoints]=\"{ '1199px': '75vw', '575px': '90vw' }\"\n>\n  @if(url) {\n    <div>\n      <app-markdown [markdownUrl]=\"url\"></app-markdown>\n    </div>\n  }\n\n  <ng-template pTemplate=\"footer\">\n    <div>\n      @if (closeButtonEnabled) {\n      <ud-button [label]=\"'Close'\" (onClick)=\"closeModal()\"></ud-button>\n      } @if (!closeButtonEnabled) {\n      <ud-button\n        [label]=\"'Accept'\"\n        (onClick)=\"navigateToLanding()\"\n        [disabled]=\"disableBtn\"\n      ></ud-button>\n      }\n    </div>\n  </ng-template>\n</p-dialog>\n", styles: [""], dependencies: [{ kind: "ngmodule", type: DialogModule }, { kind: "component", type: i3$1.Dialog, selector: "p-dialog", inputs: ["header", "draggable", "resizable", "positionLeft", "positionTop", "contentStyle", "contentStyleClass", "modal", "closeOnEscape", "dismissableMask", "rtl", "closable", "responsive", "appendTo", "breakpoints", "styleClass", "maskStyleClass", "maskStyle", "showHeader", "breakpoint", "blockScroll", "autoZIndex", "baseZIndex", "minX", "minY", "focusOnShow", "maximizable", "keepInViewport", "focusTrap", "transitionOptions", "closeIcon", "closeAriaLabel", "closeTabindex", "minimizeIcon", "maximizeIcon", "visible", "style", "position"], outputs: ["onShow", "onHide", "visibleChange", "onResizeInit", "onResizeEnd", "onDragEnd", "onMaximize"] }, { kind: "directive", type: i4.PrimeTemplate, selector: "[pTemplate]", inputs: ["type", "pTemplate"] }, { kind: "component", type: AppMarkdownComponent, selector: "app-markdown", inputs: ["markdownUrl"] }, { kind: "component", type: ButtonComponent, selector: "ud-button", inputs: ["label", "ariaLabel", "icon", "loading", "isPrimary", "noWrap", "iconPos", "palette", "disabled", "variant", "layout", "type"], outputs: ["onClick", "onFocus", "onBlur"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-terms-and-condition', standalone: true, imports: [DialogModule, AppMarkdownComponent, ButtonComponent], template: "<p-dialog\n  [showHeader]=\"true\"\n  header=\"Terms and Conditions\"\n  [modal]=\"true\"\n  [draggable]=\"false\"\n  [closeOnEscape]=\"false\"\n  [(visible)]=\"modalVisible\"\n  [closable]=\"closeButtonEnabled\"\n  (onHide) = \"closeModal()\"\n  [style]=\"{ width: '50rem' }\"\n  [breakpoints]=\"{ '1199px': '75vw', '575px': '90vw' }\"\n>\n  @if(url) {\n    <div>\n      <app-markdown [markdownUrl]=\"url\"></app-markdown>\n    </div>\n  }\n\n  <ng-template pTemplate=\"footer\">\n    <div>\n      @if (closeButtonEnabled) {\n      <ud-button [label]=\"'Close'\" (onClick)=\"closeModal()\"></ud-button>\n      } @if (!closeButtonEnabled) {\n      <ud-button\n        [label]=\"'Accept'\"\n        (onClick)=\"navigateToLanding()\"\n        [disabled]=\"disableBtn\"\n      ></ud-button>\n      }\n    </div>\n  </ng-template>\n</p-dialog>\n" }]
        }], ctorParameters: () => [{ type: i1$1.Router }, { type: i0.ElementRef }, { type: TermsAndConditionService }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }], propDecorators: { closeButtonEnabled: [{
                type: Input
            }], onClose: [{
                type: Output
            }] } });

class FooterComponent {
    constructor(environment) {
        this.environment = environment;
        this.appVersion = '';
        this.visible = false;
        this.showTermsAndConditionModal = false;
        this.currentYear = new Date().getFullYear();
        this.baseS3Url = '';
    }
    ngOnInit() {
        const environment = this.environment.getEnvironmentProperties();
        this.baseS3Url = `${environment.s3BucketBaseUrl}`;
        this.appVersion = `${environment.appVersion}`;
    }
    showDialog() {
        this.visible = true;
    }
    openTermsAndConditionModal() {
        this.showTermsAndConditionModal = true;
    }
    onCloseModal() {
        this.showTermsAndConditionModal = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FooterComponent, deps: [{ token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: FooterComponent, isStandalone: true, selector: "app-footer", ngImport: i0, template: "<div class=\"footer\">\n  <caption>\n    <img\n      [src]=\"baseS3Url + '/img/svg/accesso-logo.svg'\"\n      width=\"25\"\n      [alt]=\"'accesso logo'\"\n    />\n    <div>\u00A9 {{currentYear}} accesso Technology Group, plc</div>\n    <div>Version {{ appVersion }}</div>\n  </caption>\n  <div class=\"contact\">\n    <!-- TODO fix lint-->\n    <!-- eslint-disable @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->\n    <a target=\"_blank\" (click)=\"showDialog()\">Contact</a>\n    <!-- eslint-disable @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->\n    <a target=\"_blank\" href=\"https://www.accesso.com/privacy-policy\"\n      >Privacy Policy</a\n    >\n    <a target=\"_blank\" (click)=\"openTermsAndConditionModal()\">Terms of Use</a>\n  </div>\n</div>\n<div class=\"contact-modal\">\n  <p-dialog\n    header=\"Contact\"\n    [(visible)]=\"visible\"\n    [modal]=\"true\"\n    [style]=\"{ width: '30%' }\"\n  >\n    <div>\n      For questions, support, and credentials, please contact your Client\n      Success Manager\n    </div>\n  </p-dialog>\n</div>\n\n@if ( showTermsAndConditionModal ) {\n<app-terms-and-condition\n  [closeButtonEnabled]=\"true\"\n  (onClose)=\"onCloseModal()\"\n></app-terms-and-condition>\n}\n", styles: [".footer{display:flex;align-items:center;padding:24px 36px 24px 24px;justify-content:space-between;border-top:1px solid var(--footer-border-color, var(--g4))}.footer caption{display:flex;flex-wrap:wrap;gap:16px;font-size:12px;line-height:16px;letter-spacing:.4px;font-weight:400}.footer .contact{display:flex;flex-wrap:wrap;gap:16px}.footer .contact a{color:var(--g1);text-decoration:none}.footer .contact a:hover{color:var(--theme-secondary);cursor:pointer;text-decoration:underline}\n"], dependencies: [{ kind: "ngmodule", type: DialogModule }, { kind: "component", type: i3$1.Dialog, selector: "p-dialog", inputs: ["header", "draggable", "resizable", "positionLeft", "positionTop", "contentStyle", "contentStyleClass", "modal", "closeOnEscape", "dismissableMask", "rtl", "closable", "responsive", "appendTo", "breakpoints", "styleClass", "maskStyleClass", "maskStyle", "showHeader", "breakpoint", "blockScroll", "autoZIndex", "baseZIndex", "minX", "minY", "focusOnShow", "maximizable", "keepInViewport", "focusTrap", "transitionOptions", "closeIcon", "closeAriaLabel", "closeTabindex", "minimizeIcon", "maximizeIcon", "visible", "style", "position"], outputs: ["onShow", "onHide", "visibleChange", "onResizeInit", "onResizeEnd", "onDragEnd", "onMaximize"] }, { kind: "component", type: TermsAndConditionComponent, selector: "app-terms-and-condition", inputs: ["closeButtonEnabled"], outputs: ["onClose"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-footer', imports: [DialogModule, TermsAndConditionComponent], standalone: true, template: "<div class=\"footer\">\n  <caption>\n    <img\n      [src]=\"baseS3Url + '/img/svg/accesso-logo.svg'\"\n      width=\"25\"\n      [alt]=\"'accesso logo'\"\n    />\n    <div>\u00A9 {{currentYear}} accesso Technology Group, plc</div>\n    <div>Version {{ appVersion }}</div>\n  </caption>\n  <div class=\"contact\">\n    <!-- TODO fix lint-->\n    <!-- eslint-disable @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->\n    <a target=\"_blank\" (click)=\"showDialog()\">Contact</a>\n    <!-- eslint-disable @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->\n    <a target=\"_blank\" href=\"https://www.accesso.com/privacy-policy\"\n      >Privacy Policy</a\n    >\n    <a target=\"_blank\" (click)=\"openTermsAndConditionModal()\">Terms of Use</a>\n  </div>\n</div>\n<div class=\"contact-modal\">\n  <p-dialog\n    header=\"Contact\"\n    [(visible)]=\"visible\"\n    [modal]=\"true\"\n    [style]=\"{ width: '30%' }\"\n  >\n    <div>\n      For questions, support, and credentials, please contact your Client\n      Success Manager\n    </div>\n  </p-dialog>\n</div>\n\n@if ( showTermsAndConditionModal ) {\n<app-terms-and-condition\n  [closeButtonEnabled]=\"true\"\n  (onClose)=\"onCloseModal()\"\n></app-terms-and-condition>\n}\n", styles: [".footer{display:flex;align-items:center;padding:24px 36px 24px 24px;justify-content:space-between;border-top:1px solid var(--footer-border-color, var(--g4))}.footer caption{display:flex;flex-wrap:wrap;gap:16px;font-size:12px;line-height:16px;letter-spacing:.4px;font-weight:400}.footer .contact{display:flex;flex-wrap:wrap;gap:16px}.footer .contact a{color:var(--g1);text-decoration:none}.footer .contact a:hover{color:var(--theme-secondary);cursor:pointer;text-decoration:underline}\n"] }]
        }], ctorParameters: () => [{ type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

class NotificationsComponent {
    constructor(notificationsService) {
        this.notificationsService = notificationsService;
        this.message = [];
        this.messageSubscription = new Subscription();
    }
    ngOnInit() {
        this.subscribeToMessageNotifications();
    }
    subscribeToMessageNotifications() {
        this.messageSubscription = this.notificationsService.messageNotificationChange
            .subscribe((notification) => {
            this.message = [];
            if (notification !== undefined && !(notification instanceof Array)) {
                this.message.push(notification);
            }
            if (notification !== undefined && notification instanceof Array) {
                notification.forEach((element) => {
                    this.message.push(element);
                });
            }
        });
    }
    ngOnDestroy() {
        this.messageSubscription.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsComponent, deps: [{ token: NotificationsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: NotificationsComponent, isStandalone: true, selector: "app-notifications", ngImport: i0, template: "<div class=\"notification-card\">\n    <p-messages [(value)]=\"message\" [enableService]=\"false\"></p-messages>\n</div>\n", styles: [".notification-card{width:100%}\n"], dependencies: [{ kind: "ngmodule", type: MessagesModule }, { kind: "component", type: i2.Messages, selector: "p-messages", inputs: ["value", "closable", "style", "styleClass", "enableService", "key", "escape", "severity", "showTransitionOptions", "hideTransitionOptions"], outputs: ["valueChange", "onClose"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-notifications', standalone: true, imports: [MessagesModule], template: "<div class=\"notification-card\">\n    <p-messages [(value)]=\"message\" [enableService]=\"false\"></p-messages>\n</div>\n", styles: [".notification-card{width:100%}\n"] }]
        }], ctorParameters: () => [{ type: NotificationsService }] });

class ErrorPageComponent {
    constructor(route, authService, router) {
        this.route = route;
        this.authService = authService;
        this.router = router;
        this.type = null;
        this.title = null;
        this.desc = null;
        this.baseS3Url = '';
        this.landingPage = '';
        this.sub = new Subscription;
        this.paramSub = new Subscription;
    }
    ngOnInit() {
        this.type = this.route.snapshot.paramMap.get('type');
        this.sub = this.route.data.subscribe(param => {
            if (param['type']) {
                this.type = param['type'];
            }
            if (param['title']) {
                this.title = param['title'];
            }
            if (param['desc']) {
                this.desc = param['desc'];
            }
            if (param['landingPage']) {
                this.landingPage = param['landingPage'];
            }
        });
        this.paramSub = this.route.queryParams.subscribe(params => {
            if (params['landingPage']) {
                this.landingPage = params['landingPage'];
            }
            if (params['title']) {
                this.title = params['title'];
            }
            if (params['desc']) {
                this.desc = params['desc'];
            }
            if (params['landingPage']) {
                this.landingPage = params['landingPage'];
            }
        });
        switch (this.type) {
            case '404':
                if (!this.title) {
                    this.title = 'Page Not Found';
                }
                if (!this.desc) {
                    this.desc = "The page you were looking for doesn't exist.";
                }
                break;
            case '401':
                if (!this.title) {
                    this.title = 'Unauthorized';
                }
                if (!this.desc) {
                    this.desc = 'You are not authorized to access this page.';
                }
                break;
            case '403':
                if (!this.title) {
                    this.title = 'Forbidden';
                }
                if (!this.desc) {
                    this.desc = 'You do not have permission to access this page.';
                }
                break;
            case '500':
                if (!this.title) {
                    this.title = 'Internal server error';
                }
                if (!this.desc) {
                    this.desc = 'There wan an error. Please try agin later.';
                }
                break;
            default:
                // if (!this.type) {
                this.type = 'Ooops..';
                // }
                if (!this.title) {
                    this.title = 'Something went wrong';
                }
                if (!this.desc) {
                    this.desc = 'Looks like something went wrong. We are working on it';
                }
        }
    }
    navigateToLandingPage() {
        if (this.landingPage) {
            this.router.navigate([this.landingPage]);
        }
        else {
            this.authService.logout();
            this.router.navigate(['/']);
        }
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
        this.paramSub.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ErrorPageComponent, deps: [{ token: i1$1.ActivatedRoute }, { token: AuthService }, { token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: ErrorPageComponent, isStandalone: true, selector: "app-error-page", ngImport: i0, template: "<div class=\"error-page-container\">\n  <h3>{{title}}</h3>\n  <div class=\"body-1\">{{desc}}</div>\n  <ud-button [variant]=\"'text'\" [label]=\"'Back to home'\" (onClick)=\"navigateToLandingPage()\"></ud-button>\n</div>", styles: [":host{height:70%}:host .error-page-container{display:flex;flex-direction:column;gap:15px;align-items:center;justify-content:space-between}\n"], dependencies: [{ kind: "component", type: ButtonComponent, selector: "ud-button", inputs: ["label", "ariaLabel", "icon", "loading", "isPrimary", "noWrap", "iconPos", "palette", "disabled", "variant", "layout", "type"], outputs: ["onClick", "onFocus", "onBlur"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ErrorPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-error-page', standalone: true, imports: [ButtonComponent], template: "<div class=\"error-page-container\">\n  <h3>{{title}}</h3>\n  <div class=\"body-1\">{{desc}}</div>\n  <ud-button [variant]=\"'text'\" [label]=\"'Back to home'\" (onClick)=\"navigateToLandingPage()\"></ud-button>\n</div>", styles: [":host{height:70%}:host .error-page-container{display:flex;flex-direction:column;gap:15px;align-items:center;justify-content:space-between}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.ActivatedRoute }, { type: AuthService }, { type: i1$1.Router }] });

var ComponentType;
(function (ComponentType) {
    ComponentType["MARKDOWN"] = "MARKDOWN";
    ComponentType["SWAGGER"] = "SWAGGER";
    ComponentType["DOWNLOAD"] = "DOWNLOAD";
})(ComponentType || (ComponentType = {}));

class CardComponent {
    constructor() {
        this.isOsCard = false;
        this.isFullCard = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: CardComponent, isStandalone: true, selector: "card", inputs: { item: "item", isOsCard: "isOsCard", isFullCard: "isFullCard" }, ngImport: i0, template: "<div class=\"dp-card\" [class.os-card]=\"isOsCard\" [class.full-card]=\"isFullCard\">\n    @if (item && item.name && item.subHeader && item.iconLink) {\n        <div class=\"dp-card-detail\">\n            <div class=\"card\">\n                <h3>{{ item.name }}</h3>\n                <div class=\"subtitle-3\">{{ item.subHeader }}</div>\n            </div>\n\n            <img [src]=\"item.iconLink\" height=\"48\" alt=\"os-card\" />\n        </div>\n    }\n    <div class=\"dp-card-body\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".dp-card{display:flex;flex-direction:column;justify-content:space-between;border-radius:16px;padding:16px;gap:32px;border:1px solid var(--footer-border-color, var(--g4))}.dp-card-body{width:100%}.os-card{width:256px}.full-card{margin:16px 0;display:flex;justify-content:center;align-items:center;color:var(--footer-border-color, var(--g4));width:100%;border-radius:10px}.dp-card-detail{display:flex;justify-content:space-between;flex-direction:row;align-items:center}.dp-card-detail>div{display:flex;flex-direction:column}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'card', standalone: true, imports: [], template: "<div class=\"dp-card\" [class.os-card]=\"isOsCard\" [class.full-card]=\"isFullCard\">\n    @if (item && item.name && item.subHeader && item.iconLink) {\n        <div class=\"dp-card-detail\">\n            <div class=\"card\">\n                <h3>{{ item.name }}</h3>\n                <div class=\"subtitle-3\">{{ item.subHeader }}</div>\n            </div>\n\n            <img [src]=\"item.iconLink\" height=\"48\" alt=\"os-card\" />\n        </div>\n    }\n    <div class=\"dp-card-body\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".dp-card{display:flex;flex-direction:column;justify-content:space-between;border-radius:16px;padding:16px;gap:32px;border:1px solid var(--footer-border-color, var(--g4))}.dp-card-body{width:100%}.os-card{width:256px}.full-card{margin:16px 0;display:flex;justify-content:center;align-items:center;color:var(--footer-border-color, var(--g4));width:100%;border-radius:10px}.dp-card-detail{display:flex;justify-content:space-between;flex-direction:row;align-items:center}.dp-card-detail>div{display:flex;flex-direction:column}\n"] }]
        }], propDecorators: { item: [{
                type: Input
            }], isOsCard: [{
                type: Input
            }], isFullCard: [{
                type: Input
            }] } });

class OsCardComponent {
    openLink(link) {
        window.open(link, '_blank');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OsCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: OsCardComponent, isStandalone: true, selector: "os-card", inputs: { componentItem: "componentItem" }, ngImport: i0, template: "@if (componentItem) {\n  <card [item]=\"componentItem\" [isOsCard]=\"true\">\n    <ud-button\n      [variant]=\"'outline'\"\n      [label]=\"'Download'\"\n      layout=\"full-width\"\n      (onClick)=\"openLink(componentItem.link)\"\n    ></ud-button>\n  </card>\n}\n", styles: [""], dependencies: [{ kind: "component", type: ButtonComponent, selector: "ud-button", inputs: ["label", "ariaLabel", "icon", "loading", "isPrimary", "noWrap", "iconPos", "palette", "disabled", "variant", "layout", "type"], outputs: ["onClick", "onFocus", "onBlur"] }, { kind: "component", type: CardComponent, selector: "card", inputs: ["item", "isOsCard", "isFullCard"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OsCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'os-card', standalone: true, imports: [ButtonComponent, CardComponent], template: "@if (componentItem) {\n  <card [item]=\"componentItem\" [isOsCard]=\"true\">\n    <ud-button\n      [variant]=\"'outline'\"\n      [label]=\"'Download'\"\n      layout=\"full-width\"\n      (onClick)=\"openLink(componentItem.link)\"\n    ></ud-button>\n  </card>\n}\n" }]
        }], propDecorators: { componentItem: [{
                type: Input
            }] } });

class SwaggerDocComponent {
    constructor() {
        this.hideAuthorizeAndTryItNowButton = true;
    }
    ngAfterViewInit() {
        this.initSwaggerUI();
    }
    ngOnChanges() {
        this.initSwaggerUI();
    }
    initSwaggerUI() {
        const HideInfoUrlPartsPlugin = () => {
            return {
                wrapComponents: {
                    InfoUrl: () => () => null,
                },
            };
        };
        const DisableTryItOutPlugin = () => {
            return {
                statePlugins: {
                    spec: {
                        wrapSelectors: {
                            allowTryItOutFor: () => () => false,
                        },
                    },
                },
            };
        };
        const DisableAuthorizePlugin = () => {
            return {
                wrapComponents: {
                    authorizeBtn: () => () => null,
                },
            };
        };
        const plugins = [HideInfoUrlPartsPlugin];
        if (this.hideAuthorizeAndTryItNowButton) {
            plugins.push(DisableTryItOutPlugin);
            plugins.push(DisableAuthorizePlugin);
        }
        SwaggerUI({
            dom_id: '#swaggerDoc',
            url: this.link,
            docExpansion: 'none',
            plugins: plugins,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SwaggerDocComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: SwaggerDocComponent, isStandalone: true, selector: "swagger-doc", inputs: { link: "link", hideAuthorizeAndTryItNowButton: "hideAuthorizeAndTryItNowButton" }, usesOnChanges: true, ngImport: i0, template: "<card [isFullCard]=\"true\">\n    <div class=\"swagger-docs-display\" id=\"swaggerDoc\"></div>\n</card>\n", styles: [":host ::ng-deep .version{line-height:10px}:host ::ng-deep .swagger-ui .info{margin:0}:host ::ng-deep .swagger-ui .info .title{font-size:30px}\n"], dependencies: [{ kind: "component", type: CardComponent, selector: "card", inputs: ["item", "isOsCard", "isFullCard"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SwaggerDocComponent, decorators: [{
            type: Component,
            args: [{ selector: 'swagger-doc', standalone: true, imports: [
                        CardComponent
                    ], template: "<card [isFullCard]=\"true\">\n    <div class=\"swagger-docs-display\" id=\"swaggerDoc\"></div>\n</card>\n", styles: [":host ::ng-deep .version{line-height:10px}:host ::ng-deep .swagger-ui .info{margin:0}:host ::ng-deep .swagger-ui .info .title{font-size:30px}\n"] }]
        }], propDecorators: { link: [{
                type: Input
            }], hideAuthorizeAndTryItNowButton: [{
                type: Input
            }] } });

class SwaggerDocsComponent {
    constructor(cdr) {
        this.cdr = cdr;
        this.selectedLink = '';
    }
    ngAfterViewInit() {
        this.initializeSelection();
    }
    ngOnChanges(changes) {
        if (changes['items'] && !changes['items'].firstChange) {
            this.initializeSelection();
        }
    }
    initializeSelection() {
        this.selectedItem = this.items && this.items[0] ? this.items[0] : undefined;
        this.selectedLink = this.items && this.items[0]?.link ? this.items[0].link : '';
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSelectionChange(value) {
        if (this.selectedLink !== value) {
            this.selectedLink = value;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SwaggerDocsComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: SwaggerDocsComponent, isStandalone: true, selector: "swagger-docs", inputs: { items: "items" }, usesOnChanges: true, ngImport: i0, template: "<div>\n    <div class=\"dropdown-wrapper\">\n        <p-dropdown\n                [options]=\"items\"\n                [(ngModel)]=\"selectedLink\"\n                (onChange)=\"onSelectionChange($any($event).value)\"\n                optionLabel=\"name\"\n                optionValue=\"link\"\n                placeholder=\"Select a Spec\" />\n    </div>\n\n    @if (selectedLink) {\n        <swagger-doc\n            [link]=\"selectedLink\"\n            ></swagger-doc>\n    }\n</div>\n", styles: [".dropdown-wrapper{width:33%}:host ::ng-deep .p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover{background:#d8d8d8}\n"], dependencies: [{ kind: "component", type: SwaggerDocComponent, selector: "swagger-doc", inputs: ["link", "hideAuthorizeAndTryItNowButton"] }, { kind: "ngmodule", type: DropdownModule }, { kind: "component", type: i1$5.Dropdown, selector: "p-dropdown", inputs: ["id", "scrollHeight", "filter", "name", "style", "panelStyle", "styleClass", "panelStyleClass", "readonly", "required", "editable", "appendTo", "tabindex", "placeholder", "loadingIcon", "filterPlaceholder", "filterLocale", "variant", "inputId", "dataKey", "filterBy", "filterFields", "autofocus", "resetFilterOnHide", "checkmark", "dropdownIcon", "loading", "optionLabel", "optionValue", "optionDisabled", "optionGroupLabel", "optionGroupChildren", "autoDisplayFirst", "group", "showClear", "emptyFilterMessage", "emptyMessage", "lazy", "virtualScroll", "virtualScrollItemSize", "virtualScrollOptions", "overlayOptions", "ariaFilterLabel", "ariaLabel", "ariaLabelledBy", "filterMatchMode", "maxlength", "tooltip", "tooltipPosition", "tooltipPositionStyle", "tooltipStyleClass", "focusOnHover", "selectOnFocus", "autoOptionFocus", "autofocusFilter", "disabled", "itemSize", "autoZIndex", "baseZIndex", "showTransitionOptions", "hideTransitionOptions", "filterValue", "options"], outputs: ["onChange", "onFilter", "onFocus", "onBlur", "onClick", "onShow", "onHide", "onClear", "onLazyLoad"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SwaggerDocsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'swagger-docs', standalone: true, imports: [
                        SwaggerDocComponent,
                        DropdownModule,
                        FormsModule,
                    ], template: "<div>\n    <div class=\"dropdown-wrapper\">\n        <p-dropdown\n                [options]=\"items\"\n                [(ngModel)]=\"selectedLink\"\n                (onChange)=\"onSelectionChange($any($event).value)\"\n                optionLabel=\"name\"\n                optionValue=\"link\"\n                placeholder=\"Select a Spec\" />\n    </div>\n\n    @if (selectedLink) {\n        <swagger-doc\n            [link]=\"selectedLink\"\n            ></swagger-doc>\n    }\n</div>\n", styles: [".dropdown-wrapper{width:33%}:host ::ng-deep .p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover{background:#d8d8d8}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { items: [{
                type: Input
            }] } });

class ContentItemComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.componentType = ComponentType;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ContentItemComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: ContentItemComponent, isStandalone: true, selector: "content-item", inputs: { pageContent: "pageContent" }, ngImport: i0, template: "@if (pageContent) {\n  <div class=\"content-item-container\">\n    @switch (pageContent.componentType) {\n      @case (componentType.MARKDOWN) {\n        @for (componentContent of pageContent.componentContents; track $index) {\n          <app-markdown [markdownUrl]=\"componentContent.link\"></app-markdown>\n        }\n      }\n  \n      @case (componentType.DOWNLOAD) {\n        <div class=\"os-card-items\">\n          @for (componentContent of pageContent.componentContents; track $index) {\n            <os-card [componentItem]=\"componentContent\"></os-card>\n          }\n        </div>\n      }\n  \n      @case (componentType.SWAGGER) {\n        <div class=\"swagger-docs\">\n          <swagger-docs [items]=\"pageContent.componentContents\"></swagger-docs>\n        </div>\n      }\n    }\n  </div>\n}\n", styles: [".content-item-container{width:auto;margin-top:20px}.content-item-container .content-header{padding-bottom:4px}:host ::ng-deep .body-2 ul{padding-left:25px;margin:0}.os-card-items{display:flex;flex-wrap:wrap;gap:24px;padding-top:32px}\n"], dependencies: [{ kind: "component", type: OsCardComponent, selector: "os-card", inputs: ["componentItem"] }, { kind: "component", type: SwaggerDocsComponent, selector: "swagger-docs", inputs: ["items"] }, { kind: "component", type: AppMarkdownComponent, selector: "app-markdown", inputs: ["markdownUrl"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ContentItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'content-item', standalone: true, imports: [OsCardComponent, SwaggerDocsComponent, AppMarkdownComponent], template: "@if (pageContent) {\n  <div class=\"content-item-container\">\n    @switch (pageContent.componentType) {\n      @case (componentType.MARKDOWN) {\n        @for (componentContent of pageContent.componentContents; track $index) {\n          <app-markdown [markdownUrl]=\"componentContent.link\"></app-markdown>\n        }\n      }\n  \n      @case (componentType.DOWNLOAD) {\n        <div class=\"os-card-items\">\n          @for (componentContent of pageContent.componentContents; track $index) {\n            <os-card [componentItem]=\"componentContent\"></os-card>\n          }\n        </div>\n      }\n  \n      @case (componentType.SWAGGER) {\n        <div class=\"swagger-docs\">\n          <swagger-docs [items]=\"pageContent.componentContents\"></swagger-docs>\n        </div>\n      }\n    }\n  </div>\n}\n", styles: [".content-item-container{width:auto;margin-top:20px}.content-item-container .content-header{padding-bottom:4px}:host ::ng-deep .body-2 ul{padding-left:25px;margin:0}.os-card-items{display:flex;flex-wrap:wrap;gap:24px;padding-top:32px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { pageContent: [{
                type: Input
            }] } });

class AnchorNavigationService {
    constructor(router) {
        this.router = router;
    }
    createAnchorIDForScrolling(anchor) {
        return anchor.replace(/ /g, '-').toLowerCase();
    }
    appendIdToCurrentRoute(id) {
        const currentUrlTree = this.router.parseUrl(this.router.url);
        currentUrlTree.fragment = id;
        const newUrl = this.router.serializeUrl(currentUrlTree);
        window.history.replaceState({}, '', newUrl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AnchorNavigationService, deps: [{ token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AnchorNavigationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AnchorNavigationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.Router }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class ContentComponent {
    constructor(route, anchorNavigationService, elementRef, markdownEventService, activatedRoute) {
        this.route = route;
        this.anchorNavigationService = anchorNavigationService;
        this.elementRef = elementRef;
        this.markdownEventService = markdownEventService;
        this.activatedRoute = activatedRoute;
        this.pageContents = [];
        this.title = '';
        this.includePadding = true;
        this.verticalTabMenuItems = [];
        this.activeItem = {};
        this.hasMarkDownContent = false;
        this.verticalScrollEnabled = false;
        this.anchorScrollingInProgress = false;
        this.contentItemComponents = [];
    }
    ngOnInit() {
        if (this.pageContents.length > 0) {
            this.hasMarkDownContent = this.hasMarkdownContent(this.pageContents);
            if (this.hasMarkDownContent) {
                this.initVerticalScrollComponents();
            }
        }
        else {
            this.activatedRoute.data.subscribe((data) => {
                const pageData = data['pageData'];
                if (pageData) {
                    this.title = pageData.product.name + ' - ' + pageData.name;
                    this.pageContents = pageData.pageContents || [];
                    this.hasMarkDownContent = this.hasMarkdownContent(this.pageContents);
                    if (this.hasMarkDownContent) {
                        this.initVerticalScrollComponents();
                    }
                }
            });
        }
    }
    initVerticalScrollComponents() {
        this.markdownLoadEvent$ = this.markdownEventService.getMarkDownLoadEvent().subscribe((value) => {
            if (value) {
                this.initAnchorNavigationItems();
                this.registerVerticalTabScrollEvent();
            }
        });
    }
    hasMarkdownContent(pageContents) {
        return pageContents
            ? pageContents.some((item) => item.componentType === ComponentType.MARKDOWN)
            : false;
    }
    registerVerticalTabScrollEvent() {
        if (this.contentWrapper &&
            this.contentWrapper.nativeElement) {
            this.contentWrapper.nativeElement.addEventListener('scroll', this.handleScroll.bind(this));
        }
    }
    scrollToView(activeItemId) {
        const element = document.getElementById(activeItemId);
        if (!element) {
            return;
        }
        this.anchorScrollingInProgress = true;
        element.scrollIntoView({
            behavior: 'auto',
        });
        this.anchorNavigationService.appendIdToCurrentRoute(activeItemId);
    }
    initAnchorNavigationItems() {
        const contentItemDiv = this.getContentItemDiv();
        if (!contentItemDiv)
            return;
        const h2Tags = this.getH2Tags(contentItemDiv);
        this.setVerticalTabMenuItems(h2Tags);
        this.setActiveItem();
        this.subscribeToFragmentChanges();
    }
    getContentItemDiv() {
        return this.elementRef.nativeElement.querySelector('.markdown-wrapper');
    }
    getH2Tags(contentItemDiv) {
        return contentItemDiv.querySelectorAll('h2');
    }
    setVerticalTabMenuItems(h2Tags) {
        this.verticalTabMenuItems = Array.from(h2Tags).map((h2) => {
            const title = h2.innerText.trim();
            const id = this.anchorNavigationService.createAnchorIDForScrolling(title);
            h2.setAttribute('id', id);
            return {
                label: title,
                id: id,
                command: () => {
                    this.scrollToView(id);
                    const verticalTab = this.verticalTabMenuItems.find((item) => item.id === id);
                    if (verticalTab) {
                        this.activeItem = verticalTab;
                    }
                },
            };
        });
    }
    setActiveItem() {
        this.activeItem =
            this.verticalTabMenuItems.length > 0 ? this.verticalTabMenuItems[0] : {};
    }
    subscribeToFragmentChanges() {
        if (this.verticalTabMenuItems.length) {
            this.route.fragment.subscribe((fragment) => {
                if (fragment) {
                    this.scrollToView(fragment);
                    const verticalTab = this.verticalTabMenuItems.find((item) => item.id === fragment);
                    if (verticalTab) {
                        this.activeItem = verticalTab;
                    }
                }
            });
        }
    }
    handleScroll() {
        if (this.anchorScrollingInProgress) {
            this.anchorScrollingInProgress = false;
            return;
        }
        const contentItemDiv = this.getContentItemDiv();
        if (!contentItemDiv)
            return;
        if (!this.contentWrapper)
            return;
        const scrollPosition = this.contentWrapper.nativeElement.scrollTop;
        const h2Tags = Array.from(this.getH2Tags(contentItemDiv));
        const initialScrollerPosition = 100;
        const contentItemWindows = h2Tags.map((h2, i, array) => {
            const currentOffsetTop = h2.getBoundingClientRect().top + scrollPosition;
            const nextOffsetTop = i < array.length - 1
                ? array[i + 1].getBoundingClientRect().top + scrollPosition
                : currentOffsetTop + h2.getBoundingClientRect().height;
            return {
                id: h2.id,
                offsetTop: currentOffsetTop - initialScrollerPosition,
                offsetBottom: nextOffsetTop - initialScrollerPosition,
            };
        });
        const activeWindow = contentItemWindows.find((window) => window.offsetTop <= scrollPosition &&
            window.offsetBottom >= scrollPosition);
        if (activeWindow) {
            const verticalTab = this.verticalTabMenuItems.find((v) => v.id === activeWindow.id);
            if (verticalTab && this.activeItem.id !== verticalTab.id) {
                this.activeItem = verticalTab;
            }
        }
    }
    ngOnDestroy() {
        this.markdownLoadEvent$?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ContentComponent, deps: [{ token: i1$1.ActivatedRoute }, { token: AnchorNavigationService }, { token: i0.ElementRef }, { token: MarkdownEventService }, { token: i1$1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: ContentComponent, isStandalone: true, selector: "app-content", inputs: { pageContents: "pageContents", title: "title", includePadding: "includePadding" }, viewQueries: [{ propertyName: "contentWrapper", first: true, predicate: ["contentWrapper"], descendants: true }, { propertyName: "contentItemComponents", predicate: ContentItemComponent, descendants: true }], ngImport: i0, template: "<div class=\"content-container\">\n  <div class=\"content-data\">\n    <div class=\"content-header\">\n      <h2>{{ title }}</h2>\n    </div>\n    <div class=\"content-item\">\n      @for (item of pageContents; track $index) {\n        <content-item [pageContent]=\"item\"></content-item>\n      }\n    </div>\n  </div>\n  @if (hasMarkDownContent) {\n    <div class=\"content-vertical-tabs\">\n      <ud-tab-menu [items]=\"verticalTabMenuItems\" [vertical]=\"true\" [activeItem]=\"activeItem\"></ud-tab-menu>\n    </div>\n  }\n</div>", styles: [".content-container{width:auto;display:flex;scroll-behavior:smooth;gap:80px}.content-container .content-header h2{font-weight:400}.content-container .content-data{flex:1;min-width:0;min-height:0;overflow-x:hidden}.content-container .overview-item{margin:32px 0}.content-container .content-vertical-tabs{max-height:600px;width:200px;align-self:flex-start;position:sticky;top:0;overflow-y:auto}@media screen and (max-width: 1000px){.content-container .content-vertical-tabs{display:none}}:host ::ng-deep .content-vertical-tabs .p-tabmenu .p-tabmenu-nav .p-tabmenuitem .p-menuitem-link{font-weight:400!important}:host ::ng-deep .content-vertical-tabs .p-tabmenu .p-tabmenu-nav .p-tabmenuitem.p-highlight .p-menuitem-link{font-weight:500!important}\n"], dependencies: [{ kind: "component", type: ContentItemComponent, selector: "content-item", inputs: ["pageContent"] }, { kind: "component", type: TabMenuComponent, selector: "ud-tab-menu", inputs: ["scrollable", "items", "vertical", "activeItem", "popup", "ariaLabel", "ariaLabelledBy"], outputs: ["activeItemChange"] }, { kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-content', standalone: true, imports: [ContentItemComponent, TabMenuComponent, CommonModule], template: "<div class=\"content-container\">\n  <div class=\"content-data\">\n    <div class=\"content-header\">\n      <h2>{{ title }}</h2>\n    </div>\n    <div class=\"content-item\">\n      @for (item of pageContents; track $index) {\n        <content-item [pageContent]=\"item\"></content-item>\n      }\n    </div>\n  </div>\n  @if (hasMarkDownContent) {\n    <div class=\"content-vertical-tabs\">\n      <ud-tab-menu [items]=\"verticalTabMenuItems\" [vertical]=\"true\" [activeItem]=\"activeItem\"></ud-tab-menu>\n    </div>\n  }\n</div>", styles: [".content-container{width:auto;display:flex;scroll-behavior:smooth;gap:80px}.content-container .content-header h2{font-weight:400}.content-container .content-data{flex:1;min-width:0;min-height:0;overflow-x:hidden}.content-container .overview-item{margin:32px 0}.content-container .content-vertical-tabs{max-height:600px;width:200px;align-self:flex-start;position:sticky;top:0;overflow-y:auto}@media screen and (max-width: 1000px){.content-container .content-vertical-tabs{display:none}}:host ::ng-deep .content-vertical-tabs .p-tabmenu .p-tabmenu-nav .p-tabmenuitem .p-menuitem-link{font-weight:400!important}:host ::ng-deep .content-vertical-tabs .p-tabmenu .p-tabmenu-nav .p-tabmenuitem.p-highlight .p-menuitem-link{font-weight:500!important}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.ActivatedRoute }, { type: AnchorNavigationService }, { type: i0.ElementRef }, { type: MarkdownEventService }, { type: i1$1.ActivatedRoute }], propDecorators: { contentWrapper: [{
                type: ViewChild,
                args: ['contentWrapper']
            }], pageContents: [{
                type: Input
            }], title: [{
                type: Input
            }], includePadding: [{
                type: Input
            }], contentItemComponents: [{
                type: ViewChildren,
                args: [ContentItemComponent]
            }] } });

class LoadingIndicatorComponent {
    constructor(loadingService) {
        this.loadingService = loadingService;
        this.loading$ = this.loadingService.loading$;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoadingIndicatorComponent, deps: [{ token: LoadingService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: LoadingIndicatorComponent, isStandalone: true, selector: "app-loading-indicator", ngImport: i0, template: "@if(loading$ | async) {\n  <ud-progress-bar></ud-progress-bar>\n }", styles: [""], dependencies: [{ kind: "pipe", type: AsyncPipe, name: "async" }, { kind: "component", type: ProgressBarComponent, selector: "ud-progress-bar", inputs: ["value", "ariaLabel", "ariaLabelledBy", "mode"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoadingIndicatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-loading-indicator', standalone: true, imports: [AsyncPipe, ProgressBarComponent], template: "@if(loading$ | async) {\n  <ud-progress-bar></ud-progress-bar>\n }" }]
        }], ctorParameters: () => [{ type: LoadingService }] });

/* eslint-disable @typescript-eslint/no-unused-vars */
class PostLoginGuard {
    constructor(authService, userServiceFactory) {
        this.authService = authService;
        this.userServiceFactory = userServiceFactory;
    }
    canActivate(next, state) {
        const userService = this.userServiceFactory.getUserService(this.authService.getAuthProvider());
        return userService.getUser().pipe(map(user => {
            if (user.isAuthenticated) {
                return true;
            }
            userService.logIn();
            return false;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PostLoginGuard, deps: [{ token: AuthService }, { token: UserServiceFactory }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PostLoginGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PostLoginGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: AuthService }, { type: UserServiceFactory }] });

/* eslint-disable @typescript-eslint/no-unused-vars */
//TODO check if canActivate can be without arguments
class TermsAndConditionGuard {
    constructor(termsAndConditionService, router, authService, loggerService, loadingService) {
        this.termsAndConditionService = termsAndConditionService;
        this.router = router;
        this.authService = authService;
        this.loggerService = loggerService;
        this.loadingService = loadingService;
    }
    canActivate(next, state) {
        if (this.termsAndConditionService.acceptedLatestTerms) {
            return of(true);
        }
        else {
            // added this to make service call to checkif user accepted latest terms and condition on page refresh
            this.loadingService.loadingOn();
            return this.termsAndConditionService.getAcceptedLatest().pipe(map((latestTermsAccepted) => {
                this.loadingService.loadingOff();
                if (latestTermsAccepted && latestTermsAccepted.acceptedLatest) {
                    this.termsAndConditionService.acceptedLatestTerms = true;
                    return true;
                }
                this.router.navigate(['terms-and-condition']);
                return false;
            }), catchError(error => {
                this.loadingService.loadingOff();
                this.loggerService.error('Failed to fetch latest terms accepted:', error);
                this.router.navigate(['/error'], {
                    queryParams: {
                        type: '500',
                        title: 'Terms of Use Error',
                        desc: 'An error occurred while validating the user’s terms of use. Please try logging in again later.',
                    },
                });
                return of(false);
            }));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionGuard, deps: [{ token: TermsAndConditionService }, { token: i1$1.Router }, { token: AuthService }, { token: LoggingService }, { token: LoadingService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TermsAndConditionGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: TermsAndConditionService }, { type: i1$1.Router }, { type: AuthService }, { type: LoggingService }, { type: LoadingService }] });

class CounterComponent {
    constructor() {
        this.seconds = 0;
        this.timeEnded = new EventEmitter();
        this.timeRemaining$ = timer(0, 1000).pipe(map$1(n => (this.seconds - n) * 1000), takeWhile(n => n >= 0), tap(n => {
            if (n <= 0 && this.timeEnded) {
                this.timeEnded.emit();
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CounterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: CounterComponent, isStandalone: true, selector: "app-counter", inputs: { seconds: "seconds" }, outputs: { timeEnded: "timeEnded" }, ngImport: i0, template: "{{ timeRemaining$ | async | date:'mm:ss' }}", isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "pipe", type: i1$3.AsyncPipe, name: "async" }, { kind: "pipe", type: i1$3.DatePipe, name: "date" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CounterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'app-counter',
                    standalone: true,
                    imports: [CommonModule],
                    template: "{{ timeRemaining$ | async | date:'mm:ss' }}",
                }]
        }], propDecorators: { seconds: [{
                type: Input
            }], timeEnded: [{
                type: Output
            }] } });

var ConfirmModalAction;
(function (ConfirmModalAction) {
    ConfirmModalAction["SUBMIT"] = "submit";
    ConfirmModalAction["CANCEL"] = "cancel";
})(ConfirmModalAction || (ConfirmModalAction = {}));
const sessionModalData = {
    messageText1: 'Your session is about to expire in',
    messageText2: 'Would you like to renew it?',
    acceptText: 'Continue',
    rejectText: 'Logout',
    minutesText: 'minutes',
};

class SessionRenewalModalComponent {
    constructor(sessionService, dialogRef) {
        this.sessionService = sessionService;
        this.dialogRef = dialogRef;
        this.sessionModalData = sessionModalData;
        this.secondsCounter = 3 * 60;
        this.confirmModalAction = ConfirmModalAction;
        this.autoLogout = true;
    }
    onCounterFinish() {
        this.autoLogoutUser();
    }
    closeModal(actionType) {
        if (actionType === this.confirmModalAction.SUBMIT) {
            this.sessionService.refreshToken();
        }
        else {
            this.sessionService.logOutUser();
        }
        this.dialogRef.close();
    }
    autoLogoutUser() {
        this.sessionService.logOutUser(this.autoLogout);
        this.dialogRef.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SessionRenewalModalComponent, deps: [{ token: SessionService }, { token: i2$2.DynamicDialogRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: SessionRenewalModalComponent, isStandalone: true, selector: "app-session-renewal-modal", ngImport: i0, template: "<div class=\"modal-container\">\n    <div class=\"body-1\">\n        {{ sessionModalData.messageText1}} \n        <span class=\"counter-color\"><app-counter [seconds]=\"secondsCounter\" (timeEnded)=\"onCounterFinish()\"></app-counter>&nbsp;{{sessionModalData.minutesText}}</span>. \n        {{ sessionModalData.messageText2}}\n    </div>\n\n    <div class=\"modal-footer\">\n        <ud-button [variant]=\"'outline'\" [label]=\"sessionModalData.rejectText\" (onClick)=\"closeModal(confirmModalAction.CANCEL)\"></ud-button>\n        <ud-button [label]=\"sessionModalData.acceptText\" (onClick)=\"closeModal(confirmModalAction.SUBMIT)\"></ud-button>\n    </div>\n</div>\n", styles: [".modal-container{display:flex;flex-direction:column;gap:30px;color:var(--g0)}.modal-container .counter-color{color:var(--theme-danger)}.modal-container .modal-footer{display:flex;justify-content:flex-end;gap:10px}\n"], dependencies: [{ kind: "component", type: ButtonComponent, selector: "ud-button", inputs: ["label", "ariaLabel", "icon", "loading", "isPrimary", "noWrap", "iconPos", "palette", "disabled", "variant", "layout", "type"], outputs: ["onClick", "onFocus", "onBlur"] }, { kind: "component", type: CounterComponent, selector: "app-counter", inputs: ["seconds"], outputs: ["timeEnded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SessionRenewalModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-session-renewal-modal', standalone: true, imports: [ButtonComponent, CounterComponent], template: "<div class=\"modal-container\">\n    <div class=\"body-1\">\n        {{ sessionModalData.messageText1}} \n        <span class=\"counter-color\"><app-counter [seconds]=\"secondsCounter\" (timeEnded)=\"onCounterFinish()\"></app-counter>&nbsp;{{sessionModalData.minutesText}}</span>. \n        {{ sessionModalData.messageText2}}\n    </div>\n\n    <div class=\"modal-footer\">\n        <ud-button [variant]=\"'outline'\" [label]=\"sessionModalData.rejectText\" (onClick)=\"closeModal(confirmModalAction.CANCEL)\"></ud-button>\n        <ud-button [label]=\"sessionModalData.acceptText\" (onClick)=\"closeModal(confirmModalAction.SUBMIT)\"></ud-button>\n    </div>\n</div>\n", styles: [".modal-container{display:flex;flex-direction:column;gap:30px;color:var(--g0)}.modal-container .counter-color{color:var(--theme-danger)}.modal-container .modal-footer{display:flex;justify-content:flex-end;gap:10px}\n"] }]
        }], ctorParameters: () => [{ type: SessionService }, { type: i2$2.DynamicDialogRef }] });

class SessionService {
    constructor(oidcSecurityService, dialogService, userServiceFactory, authService, logger) {
        this.oidcSecurityService = oidcSecurityService;
        this.dialogService = dialogService;
        this.userServiceFactory = userServiceFactory;
        this.authService = authService;
        this.logger = logger;
        this.refreshBuffer = 15 * 60 * 1000; // 15 minutes in milliseconds
        this.promptBuffer = 3 * 60 * 1000; // 3 minutes in milliseconds
        this.destroy$ = new Subject();
        this.silentRenewStarted = false;
        this.idtokenExp = 0;
    }
    setupSessionRenewal() {
        this.cleanupSubscription();
        this.oidcSecurityService.getPayloadFromIdToken().pipe(takeUntil(this.destroy$), catchError$1(error => {
            this.logger.error('Failed to get token payload:', error);
            return new Observable();
        }))
            .subscribe(token => {
            if (!token && !token.exp) {
                return;
            }
            this.idtokenExp = token.exp;
            const expirationTimeInMilliseconds = token.exp * 1000;
            this.schedulePromptAndLogout(this.getPromptStartTime(expirationTimeInMilliseconds), this.getPromptEndTime(expirationTimeInMilliseconds));
        });
    }
    getPromptStartTime(tokenExpireTime) {
        const currentTime = new Date().getTime();
        return tokenExpireTime - currentTime - this.promptBuffer;
    }
    getPromptEndTime(tokenExpireTime) {
        const currentTime = new Date().getTime();
        return tokenExpireTime - currentTime;
    }
    schedulePromptAndLogout(promptStartTime, promptEndTime) {
        // Timer for session prompt
        this.promptSubscription = timer(promptStartTime).pipe(switchMap(() => {
            this.showSessionPrompt();
            return new Observable();
        }), takeUntil(timer(promptEndTime))).subscribe();
    }
    refreshToken() {
        this.oidcSecurityService.forceRefreshSession().pipe(catchError$1(error => {
            this.silentRenewStarted = false;
            this.logOutUser();
            this.logger.error('Token refresh failed:', error);
            return throwError(() => new Error('Token refresh failed'));
        })).subscribe({
            next: (loginData) => {
                this.logger.info('Token refresh successful:', loginData);
                if (loginData && loginData.idToken) {
                    const loggedInUser = { ...this.authService.user, idToken: loginData?.idToken };
                    this.authService.user = loggedInUser;
                }
                this.silentRenewStarted = false;
                this.setupSessionRenewal();
            }
        });
    }
    showSessionPrompt() {
        this.ref = this.dialogService.open(SessionRenewalModalComponent, {
            header: 'Session Expiration',
            width: '25%',
            contentStyle: { 'max-height': '500px',
                overflow: 'auto' },
            closable: false,
        });
    }
    cleanupSubscription() {
        if (this.promptSubscription) {
            this.promptSubscription.unsubscribe();
        }
    }
    initiateSilentTokenRefresh() {
        const currentTime = new Date().getTime();
        this.getTokenPayload().subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any  
            next: (token) => {
                const tokenExpiration = token?.exp * 1000;
                if (tokenExpiration && !this.silentRenewStarted &&
                    (tokenExpiration - currentTime < this.refreshBuffer)) {
                    this.silentRenewStarted = true;
                    this.refreshToken();
                }
            },
            error: (error) => {
                this.logger.error('Failed to get token payload:', error);
                this.logOutUser();
            }
        });
    }
    getTokenExpiration() {
        return this.idtokenExp * 1000;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getTokenPayload() {
        return this.oidcSecurityService.getPayloadFromIdToken();
    }
    logOutUser(autoLogout = false) {
        const message = autoLogout ? 'Your session has expired due to inactivity. Please sign in again to continue.' : undefined;
        this.userServiceFactory
            .getUserService(this.authService.getAuthProvider())
            .logOut(autoLogout, message);
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanupSubscription();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SessionService, deps: [{ token: i1.OidcSecurityService }, { token: i2$2.DialogService }, { token: UserServiceFactory }, { token: AuthService }, { token: LoggingService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SessionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SessionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.OidcSecurityService }, { type: i2$2.DialogService }, { type: UserServiceFactory }, { type: AuthService }, { type: LoggingService }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class TokenInterceptor {
    constructor(sessionService, authService, environment) {
        this.sessionService = sessionService;
        this.authService = authService;
        this.environment = environment;
    }
    intercept(request, next) {
        const apiBaseUrl = this.environment.getEnvironmentProperties().adkPortalApiBaseUrl;
        if (request.url.includes(apiBaseUrl)) {
            return this.handleTokenRequest(request, next);
        }
        else {
            return next.handle(request);
        }
    }
    handleTokenRequest(request, next) {
        this.sessionService.initiateSilentTokenRefresh();
        const idToken$ = this.authService.getIDToken();
        return idToken$.pipe(switchMap$1((idToken) => {
            if (idToken) {
                request = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${idToken}`,
                        'X-DevPortal-App': this.environment.getEnvironmentProperties().originatingApp || ''
                    },
                });
            }
            return next.handle(request);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TokenInterceptor, deps: [{ token: SessionService }, { token: AuthService }, { token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TokenInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TokenInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: SessionService }, { type: AuthService }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class GoogleAnalyticsService {
    constructor(router, logger, environment) {
        this.router = router;
        this.logger = logger;
        this.environment = environment;
    }
    initialize() {
        this.googleAnalyticsId = this.environment.getEnvironmentProperties().googleAnalyticsId;
        if (!this.googleAnalyticsId) {
            this.logger.warn('Google Analytics ID is not configured.');
            return;
        }
        this.addAnalyticsScripts();
        this.onRouteChange();
    }
    addAnalyticsScripts() {
        try {
            const baseUrl = 'https://www.googletagmanager.com/gtag/js?id=';
            this.appendScript(`${baseUrl}${this.googleAnalyticsId}`, true);
            const inlineScriptContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', '${this.googleAnalyticsId}', { 'send_page_view': false });
      `;
            this.appendInlineScript(inlineScriptContent);
        }
        catch (error) {
            this.logger.error('Error adding Google Analytics scripts:', error);
        }
    }
    appendScript(src, async) {
        const script = document.createElement('script');
        script.async = async;
        script.src = src;
        document.head.appendChild(script);
    }
    appendInlineScript(content) {
        const script = document.createElement('script');
        script.innerHTML = content;
        document.head.appendChild(script);
    }
    onRouteChange() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                if (typeof gtag === 'undefined') {
                    this.logger.warn('gtag is not defined.');
                    return;
                }
                gtag('config', this.googleAnalyticsId, {
                    page_path: event.urlAfterRedirects,
                });
            }
        });
    }
    /**
     * Tracks a custom event in Google Analytics.
     *
     * @param action - The event action (e.g., 'click', 'submit'). This is a required field and describes the type of interaction.
     * @param eventCategory - (Optional) A category to group events (e.g., 'button', 'form'). Helps in organizing related events.
     * @param eventLabel - (Optional) A label to provide additional context (e.g., 'signup-button', 'contact-form'). Useful for debugging and analytics insights.
     * @param value - (Optional) A numerical value associated with the event (e.g., 1 for success, monetary value). Used for measuring the importance or success of the event.
     *
     * This method sends a custom event to Google Analytics using the `gtag` function.
     * It ensures the `gtag` function is defined before attempting to send data, and logs a warning if `gtag` is not initialized.
     */
    event(action, eventCategory, eventLabel, value) {
        if (typeof gtag === 'undefined') {
            this.logger.warn('gtag is not defined.');
            return;
        }
        gtag('event', action, {
            ...(eventCategory && { event_category: eventCategory }),
            ...(eventLabel && { event_label: eventLabel }),
            ...(value && { value }),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: GoogleAnalyticsService, deps: [{ token: i1$1.Router }, { token: LoggingService }, { token: APP_ENVIRONMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: GoogleAnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: GoogleAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.Router }, { type: LoggingService }, { type: EnvironmentService, decorators: [{
                    type: Inject,
                    args: [APP_ENVIRONMENT]
                }] }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class TooltipDirective {
    constructor(elementRef, appRef, componentFactoryResolver, injector) {
        this.elementRef = elementRef;
        this.appRef = appRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        /**
         * Text content for the tooltip. This will be displayed when the user hovers over the element.
         */
        this.tooltip = '';
        /**
         * Position of the tooltip relative to the element.
         * Can be one of the following:
         * - TooltipPosition.ABOVE: Tooltip appears above the element
         * - TooltipPosition.BOTTOM: Tooltip appears below the element
         * - TooltipPosition.LEFT: Tooltip appears to the left of the element
         * - TooltipPosition.RIGHT: Tooltip appears to the right of the element
         * @default TooltipPosition.ABOVE
         */
        this.position = TooltipPosition.DEFAULT;
        this.componentRef = null;
    }
    onMouseEnter() {
        this.initializeTooltip();
    }
    onMouseLeave() {
        this.destroy();
    }
    initializeTooltip() {
        if (this.componentRef === null) {
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(TooltipComponent);
            this.componentRef = componentFactory.create(this.injector);
            this.appRef.attachView(this.componentRef.hostView);
            const [tooltipDOMElement] = this.componentRef.hostView.rootNodes;
            this.setTooltipComponentProperties();
            document.body.appendChild(tooltipDOMElement);
            this.showTooltip();
        }
    }
    setTooltipComponentProperties() {
        if (this.componentRef !== null) {
            this.componentRef.instance.tooltip = this.tooltip;
            this.componentRef.instance.position = this.position;
            const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();
            switch (this.position) {
                case TooltipPosition.BELOW: {
                    this.componentRef.instance.left = Math.round((right - left) / 2 + left);
                    this.componentRef.instance.top = Math.round(bottom);
                    break;
                }
                case TooltipPosition.ABOVE: {
                    this.componentRef.instance.left = Math.round((right - left) / 2 + left);
                    this.componentRef.instance.top = Math.round(top);
                    break;
                }
                case TooltipPosition.RIGHT: {
                    this.componentRef.instance.left = Math.round(right);
                    this.componentRef.instance.top = Math.round(top + (bottom - top) / 2);
                    break;
                }
                case TooltipPosition.LEFT: {
                    this.componentRef.instance.left = Math.round(left);
                    this.componentRef.instance.top = Math.round(top + (bottom - top) / 2);
                    break;
                }
                default: {
                    break;
                }
            }
        }
    }
    showTooltip() {
        if (this.componentRef !== null) {
            this.componentRef.instance.visible = true;
        }
    }
    ngOnDestroy() {
        this.destroy();
    }
    destroy() {
        if (this.componentRef !== null) {
            this.appRef.detachView(this.componentRef.hostView);
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ElementRef }, { token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.8", type: TooltipDirective, isStandalone: true, selector: "[tooltip]", inputs: { tooltip: "tooltip", position: "position" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tooltip]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }], propDecorators: { tooltip: [{
                type: Input
            }], position: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

/*
 * Public API Surface of adk-portal-shared
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_ENVIRONMENT, AuthService, ClipboardCopyComponent, ContentComponent, ContentItemComponent, EnvironmentService, ErrorPageComponent, FooterComponent, GoogleAnalyticsService, HomeComponent, LoadingIndicatorComponent, LoadingService, LogLevel, LoggingService, MarkdownEventService, NavbarComponent, NotificationsComponent, NotificationsService, OidcCallbackComponent, PostLoginGuard, SessionService, TermsAndConditionComponent, TermsAndConditionGuard, TermsAndConditionService, TokenInterceptor, TooltipComponent, TooltipDirective, TooltipPosition, UserServiceFactory };
